import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import WatchIcon from "@mui/icons-material/Watch";
import Tooltip from "@mui/material/Tooltip";
import {
  setWatchIntegrationEnabled,
  fetchWatchIntegrationEnabled,
  fetchClientHealthMetrics,
  fetchClientLatestHealthMetrics,
  fetchClientVitalsAlerts,
  fetchClientHealthSignals,
  fetchClientHealthConfig,
  latestByMetricType,
  acknowledgeVitalsAlert,
} from "../../api/watchService";
import WatchThresholdSettings from "./WatchThresholdSettings";
import ConsentStatusBadge from "../../components/consent/ConsentStatusBadge";
import { useUserPresence } from "../../Hooks/useFirebasePresence";
import {
  capturePanelFailure,
  panelLoadBreadcrumb,
} from "../../config/capturePanelFailure";
import {
  parseClientDevices,
  collapseDevicesByKind,
  DEVICE_KIND,
} from "../../utils/devicePresenceUtils";
import {
  classifyBloodPressureSeverity,
  classifyVitalSeverity,
  vitalRangeColor,
  vitalSeverityA11yLabel,
  vitalSeverityMarker,
} from "../../utils/vitalSeverity";

/**
 * #495 — the card's palette, split by ROLE rather than by severity.
 *
 * The 4px left border and the severity WORD were one constant, so they shared
 * one colour and the text inherited a value chosen for a band. A border is
 * decorative — severity is already stated in text, so the colour is redundant
 * there and carries no contrast requirement. The text is text, and `#F57C00`
 * measured ~2.7:1 on white at 0.7rem.
 *
 * Ratios are asserted in `WatchVitalsSection.contrast.test.js` rather than
 * eyeballed, so reaching for a lighter tone later fails a test instead of
 * shipping.
 */
export const SEVERITY_ACCENT_CRITICAL = "#C62828"; // border band, decorative
export const SEVERITY_ACCENT_WARNING = "#F57C00"; // border band, decorative
/** The DISABLED panel is #D9D9D9, darker than either enabled surface, and it
 *  renders exactly one paragraph. META_TEXT is 4.24 there — so that paragraph
 *  gets its own role rather than darkening every colour on the card for a
 *  pairing that occurs nowhere else. */
export const DISABLED_PANEL_TEXT = "#4B5563"; // 5.35 on #D9D9D9
/** Positive/OK copy. Was #388E3C at 4.12 white / 3.66 panel. */
export const OK_TEXT = "#256A29";
export const SEVERITY_TEXT_CRITICAL = "#C62828"; // 5.62 white / 5.00 panel
export const SEVERITY_TEXT_WARNING = "#A04A00"; // 6.04 white / 5.37 panel
/** Timestamps and other secondary copy. Was #aaa / #888 / #999 / #777. */
export const META_TEXT = "#5B6472";

/**
 * Poll cadence for the live vitals pane (ms). Watch samples arrive
 * periodically, so ~20s is "near real-time" without hammering the API.
 *
 * #627: the card promises "Retrying automatically…", so the retry must be a
 * real strategy rather than a fixed-rate poll wearing the word. Requests go out
 * at the base interval while healthy, and back off geometrically per
 * CONSECUTIVE failure — 20s, 40s, 80s … — capped at the ceiling below.
 *
 * The cap is load-bearing in both directions: it stops a struggling API being
 * hammered every 20s by every open profile, and it guarantees the card NEVER
 * stops retrying. Giving up on a care surface would leave a caregiver looking
 * at a dead panel with no indication it had stopped trying.
 */
export const VITALS_POLL_BASE_MS = 20000;
export const VITALS_POLL_MAX_MS = 320000;
/** Skipped base-intervals per consecutive failure, capped by the ceiling. */
const MAX_BACKOFF_STEPS = VITALS_POLL_MAX_MS / VITALS_POLL_BASE_MS;
/**
 * How long one attempt may hold the in-flight gate. `apiRequest` uses plain
 * `fetch` with no timeout and no abort signal (#622), so a request that never
 * settles would hold the gate for the life of the session and the card would
 * stop retrying — the one outcome the cap exists to prevent. Bounding the
 * ATTEMPT restores the guarantee even though the underlying request cannot yet
 * be cancelled. Comfortably above the backend's own 10s request timeout, so a
 * merely slow response is never concluded early.
 */
export const VITALS_REQUEST_TIMEOUT_MS = 30000;

/** How many alerts the pane fetches. Named because the collapsed summary must
 *  know when the page it is counting may be truncated (#490 gap 3). */
export const VITALS_ALERTS_LIMIT = 20;

/** Friendly labels for known HealthKit metric types. Unknown types fall back to
 *  a humanized version of the raw type, so any new watch metric still appears. */
const METRIC_LABELS = {
  heart_rate: "Heart Rate",
  resting_heart_rate: "Resting Heart Rate",
  walking_heart_rate: "Walking Heart Rate",
  heart_rate_variability: "Heart Rate Variability",
  blood_oxygen: "Blood Oxygen",
  oxygen_saturation: "Blood Oxygen",
  respiratory_rate: "Respiratory Rate",
  body_temperature: "Body Temperature",
  wrist_temperature: "Wrist Temperature",
  sleep_duration: "Sleep",
  step_count: "Steps",
  steps: "Steps",
  active_energy: "Active Energy",
  exercise_minutes: "Exercise Minutes",
  walking_speed: "Walking Speed",
  vo2_max: "VO₂ Max",
  spo2: "Blood Oxygen",
  blood_pressure_systolic: "Systolic BP",
  blood_pressure_diastolic: "Diastolic BP",
  // #538: the collapsed pair. The split keys above stay for any single-half
  // reading that reaches other consumers, but the tile grid renders this one.
  blood_pressure: "Blood Pressure",
  walking_steadiness: "Walking Steadiness",
  walking_asymmetry: "Walking Asymmetry",
  double_support_pct: "Double Support",
  step_length: "Step Length",
  stair_ascent_speed: "Stair Ascent Speed",
  stair_descent_speed: "Stair Descent Speed",
  number_of_times_fallen: "Times Fallen",
};

export const humanizeMetricType = (type) =>
  String(type || "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || "Metric";

export const metricLabel = (type) =>
  Object.prototype.hasOwnProperty.call(METRIC_LABELS, type)
    ? METRIC_LABELS[type]
    : humanizeMetricType(type);

/** Plain-language, caregiver-facing explanations of each Apple Watch vital —
 *  layman's terms plus why it matters for an elderly / disabled client. Shown as
 *  a hover/touch tooltip on each tile so a caregiver never has to guess what a
 *  metric means. Keyed by the same metricType keys as METRIC_LABELS. */
const METRIC_DESCRIPTIONS = {
  heart_rate: "Heartbeats per minute right now. About 60–100 is normal at rest.",
  resting_heart_rate:
    "Heart rate at rest. A steady climb over days can be an early sign of illness, stress, or dehydration.",
  walking_heart_rate:
    "How hard the heart works while walking. Higher than usual can mean more strain.",
  heart_rate_variability:
    "The tiny variation between heartbeats. Higher means better-rested; a drop can signal stress, illness, or poor sleep.",
  blood_oxygen:
    "Oxygen level in the blood. Below about 92% is a concern — check their breathing.",
  oxygen_saturation:
    "Oxygen level in the blood. Below about 92% is a concern — check their breathing.",
  spo2:
    "Oxygen level in the blood. Below about 92% is a concern — check their breathing.",
  respiratory_rate:
    "Breaths per minute. Faster than usual can signal breathing trouble.",
  body_temperature: "Body temperature. A rise can come before they feel sick.",
  wrist_temperature:
    "Skin temperature at the wrist. A rise can come before they feel sick.",
  vo2_max:
    "An estimate of heart-and-lung fitness. Low or falling means reduced cardiovascular fitness.",
  active_energy:
    "Calories burned from movement — a sign of how active they've been.",
  exercise_minutes: "Minutes of brisk activity today.",
  step_count:
    "Steps counted. This may be a recent chunk, not the full-day total.",
  steps: "Steps counted. This may be a recent chunk, not the full-day total.",
  walking_speed:
    "How fast they walk. Slowing over time is one of the strongest early signs of frailty (around 1.0 m/s is a common concern line).",
  step_length:
    "How far they travel per step. Shorter steps mean reduced mobility and higher fall risk.",
  double_support_pct:
    "The share of each walking cycle with BOTH feet on the ground. Steady walkers spend less time here — a higher number means a more cautious, unstable gait and higher fall risk.",
  walking_asymmetry:
    "How evenly the left and right legs move. 0% is even; higher means an uneven gait (injury, pain, or neurological) and more fall risk.",
  walking_steadiness:
    "Apple's fall-risk rating (OK / Low / Very Low) based on how steady the walk is.",
  stair_ascent_speed:
    "How fast they climb stairs. Slowing signals declining leg strength.",
  stair_descent_speed:
    "How fast they go DOWN stairs — the most fall-prone movement. Slowing signals declining balance and strength.",
  number_of_times_fallen:
    "A running count of detected falls (a trend — separate from live fall alerts).",
  blood_pressure_systolic:
    "Top blood-pressure number, from a connected cuff. High readings can trigger alerts.",
  blood_pressure_diastolic:
    "Bottom blood-pressure number, from a connected cuff. High readings can trigger alerts.",
  blood_pressure:
    "Blood pressure from a connected cuff, shown as top over bottom (for example 120/80). Around 120/80 is typical; 140/90 or higher can trigger an alert. If one number is missing it shows as a dash — that half has not been recorded.",
  sleep_duration: "How long they slept.",
};

/** Plain-language description for a metric type, or "" when we have none (the
 *  tile then renders no tooltip rather than an empty one). */
// OWN properties only — same reason as metricWhenMeasured below: a bare
// [type] lookup answers "constructor" with a function that stringifies into
// the tile's accessible name, and this value feeds the SAME ariaText chain.
export const metricDescription = (type) =>
  Object.prototype.hasOwnProperty.call(METRIC_DESCRIPTIONS, type)
    ? METRIC_DESCRIPTIONS[type]
    : "";

/**
 * GMA-1879 — WHEN the watch takes each reading, for the metrics whose absence
 * is normal rather than broken.
 *
 * A caregiver seeing a blank respiratory-rate tile has no way to know Apple
 * Watch only samples it during sleep; the tile reads as a fault, and the
 * client's own vitals arriving with twelve of nineteen types populated was
 * escalated on exactly that misreading.
 *
 * COMPLETE, not partial, and a parity test enforces that: every metric with a
 * description carries a fact here. An earlier revision of this map was
 * deliberately partial, on the reasoning that a frequently-sampled metric like
 * heart rate needs no note. That was wrong in both directions — the ticket asks
 * for full coverage, and the note on a frequently-sampled metric is what makes
 * a gap there worth escalating rather than what excuses it.
 *
 * So the facts split into two kinds, and the wording must keep them apart:
 *   absence-EXCUSING  — "only while asleep", "only when the client walks"
 *   absence-QUALIFYING — "every few minutes", "counted through the day"
 * Wording a frequently-sampled metric like the first kind would make a real
 * fault read as normal to a caregiver. That is the failure that costs someone
 * something, and it is what the render test pins.
 *
 * NOTE ON THE NAME: "cadence" already means the network POLL interval in this
 * file. This is measurement timing and must not borrow that word.
 */
const METRIC_WHEN_MEASURED = Object.freeze({
  respiratory_rate:
    "Measured only while asleep. A client who does not wear the watch overnight will never have a reading, and no permission change alters that.",
  blood_oxygen:
    "Sampled in the background only while the wrist is still, and it has its own toggle in the Watch app separate from Health sharing.",
  oxygen_saturation:
    "Sampled in the background only while the wrist is still, and it has its own toggle in the Watch app separate from Health sharing.",
  spo2:
    "Sampled in the background only while the wrist is still, and it has its own toggle in the Watch app separate from Health sharing.",
  sleep_duration: "Recorded only when the watch is worn overnight.",
  wrist_temperature:
    "Recorded overnight after several nights of wear establish a baseline.",
  vo2_max: "Estimated from outdoor walks or runs, so it updates rarely.",
  walking_speed: "Updates only when the client walks.",
  step_length: "Updates only when the client walks.",
  double_support_pct: "Updates only when the client walks.",
  walking_asymmetry: "Updates only when the client walks.",
  walking_steadiness: "Updates only when the client walks.",
  stair_ascent_speed: "Updates only when the client climbs stairs.",
  stair_descent_speed: "Updates only when the client goes down stairs.",
  blood_pressure:
    "Comes from a cuff or a manual entry — the watch does not measure it.",
  blood_pressure_systolic:
    "Comes from a cuff or a manual entry — the watch does not measure it.",
  blood_pressure_diastolic:
    "Comes from a cuff or a manual entry — the watch does not measure it.",
  // GMA-1879 AC1: every metric in METRIC_DESCRIPTIONS carries a cadence fact.
  // The continuously-measured ones need one too — for those the fact is what
  // makes a blank tile genuinely worth asking about, which is the inverse of
  // the sleep-only case but the same caregiver question.
  // NOT "measured continuously" — that is the ticket's wording and it is
  // wrong for the watch, which samples every few minutes in the background
  // and continuously only during a workout. The distinction matters here
  // more than it would in a label: this text is what a caregiver uses to
  // decide whether a gap is a fault, and "continuous" turns ordinary
  // background sampling into a reason to escalate.
  heart_rate:
    "Sampled every few minutes while the watch is worn, and continuously during a workout.",
  resting_heart_rate: "Calculated once a day from a full day of wear.",
  walking_heart_rate: "Updates only when the client walks.",
  heart_rate_variability:
    "Sampled periodically, usually while the client is still or asleep.",
  body_temperature:
    "Comes from a thermometer or a manual entry — the watch records wrist temperature instead.",
  step_count: "Counted through the day while the watch is worn.",
  steps: "Counted through the day while the watch is worn.",
  active_energy: "Counted through the day while the watch is worn.",
  exercise_minutes: "Counted through the day while the watch is worn.",
  number_of_times_fallen:
    "Recorded only when the watch detects a fall, so it stays at zero unless one happens.",
});

/** The when-measured note for a metric. Every metric with a description has
 *  one, enforced by a parity test. "" means no entry in the map — the tile
 *  still renders and is still labelled, since metricLabel falls back to
 *  humanizeMetricType; it simply carries no cadence explanation. */
export const metricWhenMeasured = (type) =>
  // OWN properties only. Object.freeze does not sever the prototype, so a bare
  // [type] lookup answers "constructor" with Object's constructor FUNCTION,
  // which stringifies into the tile's accessible name and tooltip. Backend
  // metric types are not a closed set here, so a reserved name reaching this
  // lookup is reachable rather than theoretical.
  Object.prototype.hasOwnProperty.call(METRIC_WHEN_MEASURED, type)
    ? METRIC_WHEN_MEASURED[type]
    : "";

export { METRIC_WHEN_MEASURED, METRIC_DESCRIPTIONS };

/** Per-tile freshness tier from a reading's OWN recordedAt (not the pane-level
 *  newest). Sparse metrics (gait, stairs, walking) only update when the client
 *  does that activity, so an 8-day-old tile can sit next to a live heart rate —
 *  the caregiver needs to tell them apart at a glance.
 *    'live'   < 1 hour   — real-time
 *    'today'  1–24 hours
 *    'recent' 1–7 days
 *    'older'  ≥ 7 days (or no parseable time)
 *  The 1-hour "live" boundary is shared with VITALS_FRESH_WINDOW_MS. */
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
export const metricFreshnessTier = (recordedAt, now = Date.now()) => {
  if (recordedAt === null || recordedAt === undefined || recordedAt === "") {
    return "older";
  }
  const t = new Date(recordedAt).getTime();
  if (!Number.isFinite(t)) return "older";
  const age = now - t;
  if (age <= VITALS_FRESH_WINDOW_MS) return "live";
  if (age <= DAY_MS) return "today";
  if (age < WEEK_MS) return "recent";
  return "older";
};

/** Accent color per freshness tier — used as the 4px left border on each tile
 *  and the legend dots. Color is ALWAYS paired with the tile's "Xd ago" text +
 *  the legend, so a colorblind caregiver never depends on hue alone. */
export const FRESHNESS_COLORS = Object.freeze({
  live: "#4CAF50", // green — real-time
  today: "#1976D2", // blue — earlier today
  recent: "#F5A623", // amber — this week
  older: "#9E9E9E", // grey — older / stale
});

/** The freshness key shown above the vitals grid. */
const FRESHNESS_LEGEND = [
  { tier: "live", label: "Live (last hour)" },
  { tier: "today", label: "Today" },
  { tier: "recent", label: "This week" },
  { tier: "older", label: "Older" },
];
const FRESHNESS_LEGEND_NOTE =
  "Some watch metrics (gait, stairs, walking) only update when the client does that activity, so an older time is normal, not a fault.";

/** GMA-1581: friendly labels for categorical HealthKit signals. */
const SIGNAL_LABELS = {
  ecg_result: "ECG",
  afib_episode: "Atrial Fibrillation",
  irregular_rhythm: "Irregular Rhythm",
  hypertension_notification: "Hypertension Notification",
  sleep_apnea_notification: "Sleep Apnea Notification",
  sleep_stage: "Sleep Stage",
};
export const signalLabel = (type) =>
  SIGNAL_LABELS[type] || humanizeMetricType(type);

// Signal types (or ECG categories) that warrant a caregiver's attention — shown
// with an amber accent vs. the neutral green of a normal reading.
const NOTABLE_SIGNALS = new Set([
  "afib_episode",
  "irregular_rhythm",
  "hypertension_notification",
  "sleep_apnea_notification",
]);
export const isNotableSignal = (s) =>
  NOTABLE_SIGNALS.has(s.signal_type) ||
  (s.signal_type === "ecg_result" && s.category && s.category !== "sinus");

/** GMA-1643: format a numeric vital for display — round to at most 2 decimals
 *  and drop trailing zeros, so a raw HealthKit float like 13.900562189253 shows
 *  as "13.9" while values that need precision (walking speed 1.07) keep it and
 *  integers (80 bpm) stay integers. Non-numeric / empty values pass through. */
export const formatMetricValue = (value) => {
  if (value === null || value === undefined || value === "") return value;
  // A whitespace-only string is not a real reading — `Number("  ")` is 0, which
  // would misrender as "0". Pass it through like other non-numeric values.
  if (typeof value === "string" && value.trim() === "") return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return String(Number(n.toFixed(2)));
};

/** #538: blood pressure is only a READING as a pair — 142/88 is stage-2
 *  hypertension; "142" and "88" alone are not readings. The tile grid sorts
 *  alphabetically by label, which put "Diastolic BP" at tile 3 and "Systolic BP"
 *  at tile 15 of 22 — opposite ends of the pane — and each carried its own
 *  freshness, so a week-old diastolic could be read alongside a live systolic as
 *  one reading that was never taken.
 *
 *  Collapses both halves into a single `blood_pressure` row. Pure and
 *  non-mutating (house immutability rule): returns a new array, never edits the
 *  input.
 *
 *  THE PAIRING KEY. Two halves are fused only when they came from the SAME
 *  measurement. Combining them on metric type alone reproduces the original
 *  defect in one tile instead of two: a systolic from today beside a diastolic
 *  from last week would render "142/88", which a caregiver reads as one
 *  measurement, and which nobody took. Dating the result to the older half
 *  makes that fabrication look stale — it does not make it true.
 *
 *  HealthKit stores blood pressure as an HKCorrelation whose two quantity
 *  samples share the correlation's start date, and the watch producer stamps
 *  each half with `sample.startDate` (WatchHealthKitService.swift:606), so both
 *  halves of a genuine reading reach us with an identical `recordedAt`. Equal
 *  instants are therefore evidence of one measurement, and the comparison is on
 *  the PARSED instant so a formatting difference ("...:00.000Z" vs "...:00Z")
 *  cannot split a real pair.
 *
 *  Fails closed: anything short of proof of a shared measurement renders the
 *  more recent half with an em dash for its missing counterpart, so a
 *  half-reading is visibly a half-reading rather than a fabricated whole.
 */
export const BP_SYSTOLIC = "blood_pressure_systolic";
export const BP_DIASTOLIC = "blood_pressure_diastolic";
export const BP_PAIRED = "blood_pressure";
const BP_MISSING_HALF = "\u2014";

/** One formatted half, or the em dash when there is no usable reading.
 *  `formatMetricValue` passes null / undefined / "" / whitespace / NaN THROUGH
 *  unchanged — its own tests pin that — so template interpolation stringified
 *  them straight onto the tile: a caregiver saw "null/88", "undefined/88" or
 *  "/88" where the design promises an em dash.
 *
 *  The test is "did it format to a NON-BLANK STRING", never falsiness: a real
 *  reading of 0 formats to "0" and must survive, and `0` is falsy. */
const bpHalf = (value) => {
  const shown = formatMetricValue(value);
  return typeof shown === "string" && shown.trim() !== "" ? shown : BP_MISSING_HALF;
};

const bpInstant = (row) => {
  const raw = row?.recordedAt;
  // Skip missing/empty stamps BEFORE parsing: `new Date(null)` is epoch 0, not
  // NaN, so an undated row would read as a real instant and two undated halves
  // would compare equal — fusing them with no evidence, which is precisely what
  // the pairing key fails closed on. Same guard as `newestRecordedAt`.
  if (raw === null || raw === undefined || raw === "") return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
};

/** True only with positive evidence that both halves are one measurement. */
export const isSameBloodPressureMeasurement = (sys, dia) => {
  if (!sys || !dia) return false;
  // Two devices at one instant is two readings. Only checked when BOTH rows
  // declare a source, so absent metadata never blocks a genuine pair.
  if (sys.source && dia.source && sys.source !== dia.source) return false;
  const ts = bpInstant(sys);
  const td = bpInstant(dia);
  // Only a parsed, real instant is positive evidence of one measurement. A
  // byte-identical UNPARSEABLE stamp proves nothing about the instant it
  // names — it could be a shared placeholder on two unrelated readings — so
  // it must not fall back to raw-string equality.
  return ts !== null && td !== null && ts === td;
};

/** The more recently recorded of two rows; `a` wins when neither can be ordered. */
const bpMoreRecent = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const ta = bpInstant(a);
  const tb = bpInstant(b);
  if (ta === null && tb === null) return a;
  if (ta === null) return b;
  if (tb === null) return a;
  return ta >= tb ? a : b;
};

export const pairBloodPressure = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const sys = list.find((r) => r && r.metricType === BP_SYSTOLIC);
  const dia = list.find((r) => r && r.metricType === BP_DIASTOLIC);
  if (!sys && !dia) return [...list];

  const rest = list.filter(
    (r) => r && r.metricType !== BP_SYSTOLIC && r.metricType !== BP_DIASTOLIC,
  );

  if (isSameBloodPressureMeasurement(sys, dia)) {
    return [
      ...rest,
      {
        metricType: BP_PAIRED,
        value: `${bpHalf(sys.value)}/${bpHalf(dia.value)}`,
        unit: sys.unit || dia.unit || "mmHg",
        recordedAt: sys.recordedAt,
      },
    ];
  }

  // No shared measurement: show the half we actually have, dated to itself.
  // Its counterpart is absent rather than borrowed from another reading.
  const keep = bpMoreRecent(sys, dia);
  const shown = bpHalf(keep.value);
  return [
    ...rest,
    {
      metricType: BP_PAIRED,
      value:
        keep.metricType === BP_SYSTOLIC
          ? `${shown}/${BP_MISSING_HALF}`
          : `${BP_MISSING_HALF}/${shown}`,
      unit: keep.unit || "mmHg",
      recordedAt: keep.recordedAt,
    },
  ];
};

/** "just now" / "5m ago" / "2h ago" / "3d ago" from a timestamp. */
export const formatRelativeTime = (iso, now = Date.now()) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
};

/** GMA-1643: a Watch reading older than this window means the pane must NOT
 *  claim to be "Live". The client's watch samples periodically and syncs to the
 *  backend; a reading within the last hour is treated as near-real-time, while
 *  anything older tells the caregiver the data is no longer current (the common
 *  failure mode: the iPhone producer stopped syncing days ago). 60 minutes. */
export const VITALS_FRESH_WINDOW_MS = 60 * 60 * 1000;

/** GMA-821: how long since the watch's live connection was last seen before the
 *  status badge calls it "Off" rather than "Idle". Firebase presence goes stale
 *  after only ~10 min of no heartbeat — normal for a backgrounded watch still on
 *  the wrist — so a short gap reads as "Idle", and only a long gap (or no known
 *  last-seen time) reads as "Off". 2 hours. */
export const WATCH_OFFLINE_STALE_MS = 2 * 60 * 60 * 1000;

/** GMA-821: honest status label for the watch's live connection.
 *  online → "On"; last-seen > 2h ago or unknown → "Off"; otherwise "Idle"
 *  (recently stale — the watch is likely on the wrist with the app asleep). */
export const watchStatusLabel = (device, now = Date.now()) => {
  if (!device) return "";
  if (device.isOnline) return "On";
  const last = device.lastSeenMs;
  const hasLastSeen = typeof last === "number" && Number.isFinite(last);
  if (!hasLastSeen || now - last > WATCH_OFFLINE_STALE_MS) return "Off";
  return "Idle";
};

/** GMA-1643: newest `recordedAt` (epoch ms) across the shown vitals, or null
 *  when none is parseable. Freshness is derived from the DATA, not the fetch
 *  time — a successful poll that only returns 9-day-old readings is not "live". */
export const newestRecordedAt = (vitals) => {
  let newest = null;
  for (const v of Array.isArray(vitals) ? vitals : []) {
    const raw = v?.recordedAt;
    // Skip missing/empty timestamps BEFORE parsing: `new Date(null)` is epoch 0
    // (not NaN), so an all-null list would otherwise report a 1970 reading and
    // read as "Stale · last reading 56y ago" instead of "no data".
    if (raw === null || raw === undefined || raw === "") continue;
    const t = new Date(raw).getTime();
    if (Number.isFinite(t) && (newest === null || t > newest)) newest = t;
  }
  return newest;
};

/** GMA-1643: freshness of the pane derived from the newest reading time:
 *  'live'  — the newest reading is within VITALS_FRESH_WINDOW_MS
 *  'stale' — there is a reading, but it is older than the window
 *  'none'  — no parseable reading yet
 *  This replaces the old "updated {fetch time}" indicator, which read
 *  "just now" on every poll even when the newest reading was days old. */
export const vitalsFreshness = (newest, now = Date.now()) => {
  if (newest === null || newest === undefined) return "none";
  const t = Number(newest);
  // Require a finite timestamp — NaN and ±Infinity are not real reading times.
  if (!Number.isFinite(t)) return "none";
  return now - t <= VITALS_FRESH_WINDOW_MS ? "live" : "stale";
};

/** Issue #633: only unacknowledged alerts are shown in the pane. Cleared
 *  alerts stay in the DB (who + when) as the audit trail — they are simply
 *  hidden here. Safe on non-array input. */
export const visibleAlerts = (alerts) =>
  Array.isArray(alerts) ? alerts.filter((a) => !a?.acknowledgedAt) : [];

/**
 * #490 gap 3 — collapse repeated alerts of the same kind into one row.
 *
 * Nine near-identical HR warnings over eight days render as nine near-identical
 * orange rows, and a caregiver who scrolls past nine of them learns to scroll
 * past orange rows — precisely what must not happen before a real event. This
 * collapses the PRESENTATION only: every alert stays a member of its group,
 * keeps its own Clear button, and is untouched in the record and the export.
 *
 * Grouping is by kind across the whole list rather than only over adjacent
 * rows. Nine HR warnings interleaved with one blood-pressure alert are still
 * nine rows under adjacency-only grouping, which would leave the fatigue this
 * is meant to remove.
 */
/**
 * Which way a reading breached, from its own threshold. "unknown" when either
 * number is missing — those alerts group together and carry no extreme.
 */
const breachDirection = (a) => {
  if (!Number.isFinite(a?.value) || !Number.isFinite(a?.threshold)) return "unknown";
  return a.value >= a.threshold ? "high" : "low";
};

/**
 * The group identity must be everything the summary row ASSERTS, or the summary
 * can lie about its own members.
 *
 * Threshold and direction are part of it, not just metric/event/severity:
 *   - `abnormal_heart_rate` is a GENERIC event and is live in this codebase's
 *     fixtures, so a 42 bpm bradycardia and a 126 bpm tachycardia share
 *     metric+event+severity. Grouped together they render as one row reading
 *     "Heart Rate above 110 bpm, peak 126" and the low reading disappears —
 *     the precise failure this feature exists to prevent.
 *   - thresholds are per-client configurable and change over time, so one run
 *     can span two of them; a single "above 110" headline is then wrong for
 *     whichever member was measured against 120.
 */
const alertKind = (a) =>
  [
    a?.metricType ?? "",
    a?.eventType ?? "",
    a?.severity ?? "",
    a?.threshold ?? "",
    // The summary asserts a unit in both the threshold label and the extreme,
    // so two runs recorded in different units cannot share a row — their values
    // are not comparable and one headline would misdescribe the other.
    a?.unit ?? "",
    breachDirection(a),
  ].join("|");

/**
 * The worst reading in a run, which is NOT always the largest.
 *
 * A high-heart-rate run breaches upward so the worst is the max; a low-oxygen
 * run breaches downward so the worst is the min. Taking max() unconditionally
 * would headline 89% — the least concerning reading of the run — as the peak.
 *
 * Direction comes from each reading's own relationship to its threshold. With
 * no threshold there is nothing to derive it from, so this returns null and the
 * caller shows a count with no extreme: a missing figure is honest, a guessed
 * one is a fabricated headline on a safety screen.
 */
const groupExtreme = (members) => {
  const scored = members.filter(
    (m) => Number.isFinite(m?.value) && Number.isFinite(m?.threshold),
  );
  if (scored.length === 0) return null;

  // Every member shares a direction by construction — it is part of the group
  // key — so reading it off the first is safe here rather than a guess.
  const high = breachDirection(scored[0]) === "high";
  const worst = scored.reduce((acc, m) =>
    high ? (m.value > acc.value ? m : acc) : m.value < acc.value ? m : acc,
  );
  return { label: high ? "peak" : "low", value: worst.value, unit: worst.unit ?? null };
};

const alertTime = (a) => {
  const t = Date.parse(a?.alertedAt ?? "");
  return Number.isNaN(t) ? null : t;
};

export const groupVisibleAlerts = (alerts, fetchedCount = null) => {
  if (!Array.isArray(alerts)) return [];

  // The card counts the page it was given, not the client's whole history. When
  // the fetch came back full, any group may be missing older members, so the
  // summary says "at least N" rather than asserting a total it cannot know.
  const maybeTruncated =
    Number.isFinite(fetchedCount) && fetchedCount >= VITALS_ALERTS_LIMIT;

  const byKind = new Map();
  alerts.forEach((a) => {
    const key = alertKind(a);
    if (!byKind.has(key)) byKind.set(key, []);
    byKind.get(key).push(a);
  });

  const groups = [...byKind.entries()].map(([key, members]) => {
    const times = members.map(alertTime).filter((t) => t !== null);
    const newest = times.length ? Math.max(...times) : null;
    // A span must cover every occurrence it is quoted beside. With an undated
    // member the count and the interval describe different sets, and
    // "3 times in 8 days" would place an occurrence that cannot be placed.
    // Recency survives — lastAt is the newest KNOWN time either way.
    const allDated = times.length === members.length;
    const oldest = allDated && times.length ? Math.min(...times) : null;
    return {
      key,
      members,
      count: members.length,
      countIsPartial: maybeTruncated,
      extreme: groupExtreme(members),
      firstAt: oldest === null ? null : new Date(oldest).toISOString(),
      lastAt: newest === null ? null : new Date(newest).toISOString(),
      _newest: newest ?? -Infinity,
    };
  });

  // Most recently active kind first — a run that fired an hour ago outranks one
  // that stopped days ago, whatever order the API returned.
  groups.sort((a, b) => b._newest - a._newest);
  // eslint-disable-next-line no-unused-vars
  return groups.map(({ _newest, ...g }) => g);
};

/**
 * The one-line headline for a collapsed run — "Heart Rate above 110 bpm".
 * Built from the metric label and the group's own threshold rather than from a
 * member's message, because each member's message names its OWN reading
 * ("Heart rate is 111 bpm") and quoting one of nine would be arbitrary.
 */
export const alertGroupLabel = (member) => {
  const label = metricLabel(member?.metricType);
  if (!Number.isFinite(member?.value) || !Number.isFinite(member?.threshold)) {
    return label;
  }
  const dir = member.value >= member.threshold ? "above" : "below";
  const unit = member.unit ? ` ${member.unit}` : "";
  // Same rounding as every other reading on this card — a HealthKit float
  // like 13.900562189253 must not appear verbatim in the summary.
  return `${label} ${dir} ${formatMetricValue(member.threshold)}${unit}`;
};

/** "9 times in 8 days" — the span a group covers, or null for a single instant. */
export const alertGroupSpan = (group) => {
  const first = Date.parse(group?.firstAt ?? "");
  const last = Date.parse(group?.lastAt ?? "");
  if (Number.isNaN(first) || Number.isNaN(last)) return null;
  const hours = Math.round((last - first) / (60 * 60 * 1000));
  if (hours <= 0) return null;
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
};

/**
 * #490 gap 3 — the collapsed summary for a run of same-kind alerts. Carries the
 * count, the span, and the worst reading, and expands to the individual rows so
 * nothing is hidden from the caregiver — only de-duplicated on screen.
 */
const AlertGroupSummary = ({ group, expanded, onToggle }) => {
  const first = group.members[0];
  const critical = first.severity === "critical";
  const accent = critical ? SEVERITY_ACCENT_CRITICAL : SEVERITY_ACCENT_WARNING;
  const severityText = critical ? SEVERITY_TEXT_CRITICAL : SEVERITY_TEXT_WARNING;
  const span = alertGroupSpan(group);
  const extreme = group.extreme
    ? `${group.extreme.label} ${formatMetricValue(group.extreme.value)}${
        group.extreme.unit ? ` ${group.extreme.unit}` : ""
      }`
    : null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "8px",
        backgroundColor: "#fff",
        border: "1px solid #eee",
        borderLeft: `4px solid ${accent}`,
        borderRadius: "6px",
        padding: "8px 10px",
        fontSize: "0.85rem",
      }}
    >
      <span
        style={{
          fontWeight: 700,
          textTransform: "uppercase",
          fontSize: "0.7rem",
          color: severityText,
          whiteSpace: "nowrap",
        }}
      >
        {critical ? "Critical" : "Warning"}
      </span>
      <span style={{ color: "#333", flex: 1 }}>
        {alertGroupLabel(first)} —{" "}
        {group.countIsPartial ? "at least " : ""}
        {group.count} times{span ? ` in ${span}` : ""}
        {extreme ? `, ${extreme}` : ""}
      </span>
      <span style={{ color: META_TEXT, fontSize: "0.75rem", whiteSpace: "nowrap" }}>
        {formatRelativeTime(group.lastAt)}
      </span>
      <button
        type="button"
        data-testid={`vitals-alert-group-toggle-${group.key}`}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Hide" : "Show"} ${group.count} ${
          critical ? "critical" : "warning"
        } ${alertGroupLabel(first)} alerts`}
        onClick={onToggle}
        style={{
          border: "1px solid #ddd",
          borderRadius: "4px",
          backgroundColor: "#fafafa",
          color: "#555",
          fontSize: "0.7rem",
          fontWeight: 600,
          padding: "2px 8px",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {expanded ? "Hide" : `Show ${group.count}`}
      </button>
    </div>
  );
};

/**
 * GMA-1571 — Apple Watch staff pane.
 * Admin manages the on/off toggle (mirrors Xandar / Pontosense). When enabled,
 * the pane polls the client's health metrics and shows every vital in
 * near-real-time for admins and assigned caregivers alike.
 */
export default function WatchVitalsSection({
  clientId,
  clientName = "this client",
  integrationEnabled = false,
  canManageIntegration = false,
  onIntegrationChanged,
  // #624: the parent already owns the authoritative GET for this client, so
  // this section must not ask the same question again. `null` means the
  // parent's fetch has not settled yet — NOT that the flag is false.
  parentResolvesEnabled = false,
  authoritativeEnabled = null,
}) {
  const [enabled, setEnabled] = useState(integrationEnabled);
  const [toggling, setToggling] = useState(false);
  // GMA-1865: block the switch until the authoritative status GET settles so a
  // late GET cannot overwrite a PATCH (Copilot race).
  const [statusSettled, setStatusSettled] = useState(false);
  const [vitals, setVitals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [signals, setSignals] = useState([]);
  const [customThresholds, setCustomThresholds] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // #627 backoff state. Refs, not state: the cadence must not re-render the
  // card, and the interval callback needs the CURRENT value, not the one
  // captured when the effect last ran.
  const consecutiveFailures = useRef(0);
  const ticksToSkip = useRef(0);
  // Attempts currently in flight. A COUNTER, not a boolean: a client switch can
  // leave the previous client's request pending, and a boolean cleared by that
  // straggler would re-open the gate while the new client's attempt is still
  // running. Decremented in `loadVitals`'s finally, so it self-balances.
  const inFlight = useRef(0);
  // Polling-session generation, mirroring `thresholdGen`. A disable/re-enable
  // (or client switch) starts a new session; attempts from a previous one must
  // not commit results, move the cadence, or hold the in-flight gate — a hung
  // request from a dead session would otherwise keep the gate closed forever
  // and the card would silently stop retrying, which is the one outcome the
  // backoff cap exists to prevent.
  const pollGen = useRef(0);
  /**
   * Advance the cadence one failure. Shared by the rejection path and the
   * attempt timeout so a hung request and a failed one back off identically —
   * 1 failure -> retry after 2 intervals, 2 -> 4, 3 -> 8 … capped.
   */
  const recordFailure = useCallback(() => {
    consecutiveFailures.current += 1;
    const steps = Math.min(
      2 ** consecutiveFailures.current,
      MAX_BACKOFF_STEPS,
    );
    ticksToSkip.current = steps - 1;
  }, []);
  // Issue #633: alert ids with a clear request in flight (disables the button).
  const [clearingAlertIds, setClearingAlertIds] = useState(() => new Set());
  // #490 gap 3: which collapsed alert runs the caregiver has expanded.
  const [expandedAlertKeys, setExpandedAlertKeys] = useState(() => new Set());
  const toggleAlertGroup = useCallback((key) => {
    setExpandedAlertKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const isMounted = useRef(true);
  // #459: mirror WatchThresholdSettings — a late fetch for client A must never
  // commit under client B after a profile client switch.
  const activeClientId = useRef(clientId);
  const statusGen = useRef(0);
  // #581 review: generation guard for the threshold config fetch (see
  // loadThresholds) — the enabled-client effect and the settings onSaved
  // refresh both target the SAME client, so activeClientId cannot order them.
  const thresholdGen = useRef(0);
  const toggleStarted = useRef(false);

  // GMA-1865: do NOT re-sync from the roster prop on every change — a stale
  // refetchClients payload (missing appleWatchEnabled) was snapping the admin
  // toggle back after a successful PATCH. Load the authoritative GET instead.
  useEffect(() => {
    activeClientId.current = clientId;
    setEnabled(integrationEnabled);
    setStatusSettled(false);
    toggleStarted.current = false;
    setVitals([]);
    setAlerts([]);
    setSignals([]);
    setCustomThresholds({});
    // #490 gap 3: the expansion key is the alert KIND, not the client, so a
    // group left expanded for one client would render pre-expanded for the
    // next — showing rows this caregiver never chose to open.
    setExpandedAlertKeys(new Set());
    setLoadError(false);
    setLoading(false);
    if (!clientId) {
      setStatusSettled(true);
      return undefined;
    }
    const gen = ++statusGen.current;
    let cancelled = false;
    // #624: one owner per question. The parent resolves this for caregivers
    // and re-polls it, so our own GET duplicated it on MOUNT. Precisely on
    // mount, not on every poll tick — this effect is keyed `[clientId]`, so an
    // ordinary tick never re-ran it. A tick still causes the duplicate when it
    // flips the flag false->true and `showWatchPane` mounts the pane for the
    // first time. The admin path is unchanged: the parent does not fetch for
    // admins, so `parentResolvesEnabled` is false and we still do.
    if (parentResolvesEnabled) {
      setStatusSettled(authoritativeEnabled !== null);
      if (authoritativeEnabled !== null) setEnabled(Boolean(authoritativeEnabled));
      return () => {
        cancelled = true;
      };
    }
    fetchWatchIntegrationEnabled(clientId)
      .then((on) => {
        if (
          cancelled ||
          activeClientId.current !== clientId ||
          gen !== statusGen.current ||
          toggleStarted.current
        ) {
          return;
        }
        setEnabled(on);
        setStatusSettled(true);
      })
      .catch(() => {
        // Keep the roster hint; never blank the admin toggle on a read failure.
        if (
          cancelled ||
          activeClientId.current !== clientId ||
          gen !== statusGen.current ||
          toggleStarted.current
        ) {
          return;
        }
        setStatusSettled(true);
      });
    return () => {
      cancelled = true;
    };
    // integrationEnabled is only the initial roster hint for this clientId.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- GMA-1865
  }, [clientId]);

  // #624: the parent re-polls every 20s, so a later value must reach the gate
  // without this section issuing a request of its own. Guarded on
  // `toggleStarted` for the same reason the GET path is: a late parent value
  // must not overwrite an admin's in-flight PATCH.
  useEffect(() => {
    if (!parentResolvesEnabled || authoritativeEnabled === null) return;
    if (toggleStarted.current) return;
    setEnabled(Boolean(authoritativeEnabled));
    setStatusSettled(true);
  }, [parentResolvesEnabled, authoritativeEnabled]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadVitals = useCallback(async (gen = pollGen.current) => {
    if (!clientId) return;
    const requestClientId = clientId;
    const requestGen = gen;
    setLoading(true);
    inFlight.current += 1;
    // One attempt releases the gate exactly once, whether it settles or is
    // concluded by the timeout below. `concluded` also stops a late response
    // committing results for an attempt the cadence has already written off.
    let concluded = false;
    const release = () => {
      if (concluded) return;
      concluded = true;
      // A straggler from a previous session must not decrement a counter it no
      // longer belongs to — the new session zeroed it.
      if (requestGen === pollGen.current) {
        inFlight.current = Math.max(0, inFlight.current - 1);
      }
    };
    const timeoutId = setTimeout(() => {
      if (concluded) return;
      release();
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId ||
        requestGen !== pollGen.current
      ) {
        return;
      }
      // #621 (review): a HUNG request produces the same caregiver-visible
      // error card as a rejection, but never reaches the catch below — the
      // promise may stay pending forever. Without this it is the one visible
      // failure mode that stays invisible to Sentry. `release()` above has
      // already set `concluded`, and the catch returns on `concluded`, so a
      // late rejection cannot report the same attempt twice.
      capturePanelFailure(
        Object.assign(
          new Error(
            `Watch vitals request timed out after ${VITALS_REQUEST_TIMEOUT_MS}ms`,
          ),
          { name: "VitalsTimeout" },
        ),
        {
          panel: "watch-vitals",
          surfaceKey: "vitals",
          op: "timeout",
          clientId: requestClientId,
        },
      );
      setLoadError(true);
      recordFailure();
    }, VITALS_REQUEST_TIMEOUT_MS);
    try {
      // GMA-1579/1581: the alert history + categorical signals are secondary —
      // never let them break the live vitals view (e.g. before deploy on an env).
      const [data, alertData, signalData] = await Promise.all([
        // #437 (cortex-be#639): the latest-per-type endpoint is burst-proof —
        // a sync burst of one metric type can no longer crowd other types out
        // of the window. Pass days=90 (BE clamps 1–90; service default is 7)
        // so last-known readings across the full API window still populate
        // GMA-1643 stale tiles instead of an empty success ("Waiting / No vitals").
        // Fall back to the windowed fetch + client-side reduction until the
        // backend endpoint is deployed on this env.
        fetchClientLatestHealthMetrics(clientId, { days: 90 }).catch(() =>
          fetchClientHealthMetrics(clientId, { limit: 100 }),
        ),

        fetchClientVitalsAlerts(clientId, { limit: VITALS_ALERTS_LIMIT }).catch(
          () => ({ alerts: [] }),
        ),
        fetchClientHealthSignals(clientId).catch(() => []),
      ]);
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId ||
        requestGen !== pollGen.current ||
        concluded
      ) {
        return;
      }
      const latest = latestByMetricType(data?.metrics || []);
      // #538: collapse the two blood-pressure halves BEFORE sorting, so the
      // pair renders as one tile with one timestamp instead of landing at
      // opposite ends of the alphabetical grid.
      const rows = pairBloodPressure(Array.from(latest.values())).sort((a, b) =>
        metricLabel(a.metricType).localeCompare(metricLabel(b.metricType)),
      );
      setVitals(rows);
      setAlerts(Array.isArray(alertData?.alerts) ? alertData.alerts : []);
      setSignals(Array.isArray(signalData) ? signalData : []);
      setLoadError(false);
      panelLoadBreadcrumb("vitals", true, {
        clientId: requestClientId,
        rowCount: rows.length,
      });
      // Recovered: back to the base cadence immediately.
      consecutiveFailures.current = 0;
      ticksToSkip.current = 0;
    } catch (err) {
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId ||
        requestGen !== pollGen.current ||
        concluded
      ) {
        return;
      }
      // Review finding: `err` must NOT go to console here. Sentry's default
      // Breadcrumbs integration records console calls, and this file's only
      // breadcrumb scrubber (`scrubEventUrls`) covers the URL-SHAPED part of a
      // console message. `err.message` is built from the response body by
      // `parseJsonOrThrow`, so it is arbitrary backend prose that may carry
      // alert content — and the capture two statements below transmits the
      // breadcrumb trail with the event. Stripping the message from the report
      // while leaving it in a breadcrumb on the same event closes nothing.
      // Log the class and status, which is what we report anyway.
      console.error(
        "Failed to load watch vitals",
        err?.name,
        err?.status ?? "network",
      );
      // #621: this is the caregiver-visible failure — the card renders its error
      // state from `loadError` right below. PanelErrorBoundary cannot see it
      // (its own docstring says so): the boundary observes THROWS, and this
      // catch consumes the rejection.
      //
      // Placed AFTER the mount/generation guards: they `return` for a
      // superseded request, so anything reaching here is a failure the caregiver
      // is actually shown. Not pinned by a test — see the suite's docblock for
      // the route I tried and why it could not discriminate.
      capturePanelFailure(err, {
        panel: "watch-vitals",
        surfaceKey: "vitals",
        op: "rejected",
        clientId: requestClientId,
      });
      setLoadError(true);
      recordFailure();
    } finally {
      clearTimeout(timeoutId);
      release();
      if (
        isMounted.current &&
        activeClientId.current === requestClientId &&
        requestGen === pollGen.current
      ) {
        setLoading(false);
      }
    }
  }, [clientId]);

  // Thresholds are NOT part of the 20s poll: the config endpoint is admin-only
  // (GMA-1588), so polling it hammers non-admin caregivers with a rejected
  // request every cycle for data that only changes when an admin saves the
  // sliders. Fetch once per client; the threshold settings save path refreshes
  // it via onSaved. Non-admins keep engine defaults until the backend exposes
  // effective thresholds on a read-authorized response (cortex-be follow-up).
  const loadThresholds = useCallback(async () => {
    if (!clientId) return;
    const requestClientId = clientId;
    // Same-client requests race: this runs from the enabled-client effect AND
    // from the settings onSaved refresh, so activeClientId alone lets a slow
    // initial response land AFTER a post-save one and revert the admin's new
    // bands. Commit only the newest request, as the status GET does.
    const gen = ++thresholdGen.current;
    let healthConfig;
    try {
      healthConfig = await fetchClientHealthConfig(clientId);
    } catch (err) {
      // A transient config failure must not wipe bands already in force — that
      // silently repaints a critical reading green until the next remount.
      // Keep the last known thresholds; a client switch resets them separately.
      // Same reason as the load catch above, and worse here: this path does
      // not capture, so the console breadcrumb sits on the scope and rides out
      // with whatever event is captured NEXT — misattributed as well as leaky.
      console.error(
        "Failed to load watch vitals thresholds",
        err?.name,
        err?.status ?? "network",
      );
      return;
    }
    if (
      !isMounted.current ||
      activeClientId.current !== requestClientId ||
      gen !== thresholdGen.current
    ) {
      return;
    }
    setCustomThresholds(
      healthConfig?.customThresholds &&
        typeof healthConfig.customThresholds === "object"
        ? healthConfig.customThresholds
        : {},
    );
  }, [clientId]);

  // Poll only while enabled — fetch immediately, then on an interval.
  useEffect(() => {
    if (!enabled || !clientId) return undefined;
    loadThresholds();
    // A fresh client (or a re-enable) starts at the base cadence — the previous
    // client's outage must not slow this one's first readings down.
    consecutiveFailures.current = 0;
    ticksToSkip.current = 0;
    // New session: any attempt still pending from the previous one is now
    // orphaned and can neither release nor hold this session's gate.
    const gen = ++pollGen.current;
    inFlight.current = 0;
    loadVitals(gen);
    // The timer stays at the base interval and the BACKOFF GATES THE REQUEST.
    // Rescheduling the timer itself would mean the delay is read when the
    // previous run is scheduled rather than when it fails, which is one poll
    // stale on exactly the transition that matters.
    const id = setInterval(() => {
      // Never overlap. A request slower than the base interval is exactly the
      // outage case the backoff exists for — without this the timer would keep
      // firing at the base rate while an attempt hangs, and overlapping results
      // could settle out of order and corrupt the consecutive-failure count.
      // The skip counter is not consumed here: the wait is already happening.
      if (gen !== pollGen.current) return;
      if (inFlight.current > 0) return;
      if (ticksToSkip.current > 0) {
        ticksToSkip.current -= 1;
        return;
      }
      loadVitals(gen);
    }, VITALS_POLL_BASE_MS);
    return () => {
      clearInterval(id);
      // Retire the session on TEARDOWN, not merely when the next one starts.
      // A request settling after the integration is disabled — with no
      // re-enable yet to bump the generation — would otherwise still pass every
      // guard and commit vitals, the error state and the cadence for a session
      // that is no longer active.
      pollGen.current += 1;
    };
  }, [enabled, clientId, loadThresholds, loadVitals]);

  const handleToggle = async () => {
    if (toggling || !statusSettled || !clientId) return;
    const next = !enabled;
    toggleStarted.current = true;
    setToggling(true);
    try {
      await setWatchIntegrationEnabled(clientId, next);
      setEnabled(next);
      toast.success(
        `Apple Watch ${next ? "enabled" : "disabled"} for ${clientName}`,
      );
      try {
        await onIntegrationChanged?.(next);
      } catch (cbErr) {
        console.warn("Watch onIntegrationChanged callback failed", cbErr);
      }
    } catch (err) {
      toast.error(err.message || "Error updating Apple Watch setting");
    } finally {
      setToggling(false);
    }
  };

  // Issue #633: clear (acknowledge) an alert. The backend records who cleared
  // it and when; on success the alert drops out of the visible list.
  const handleClearAlert = async (alert) => {
    if (!clientId || clearingAlertIds.has(alert.id)) return;
    setClearingAlertIds((prev) => new Set(prev).add(alert.id));
    try {
      const data = await acknowledgeVitalsAlert(clientId, alert.id);
      if (!isMounted.current) return;
      const ackAt = data?.alert?.acknowledgedAt || new Date().toISOString();
      const ackBy = data?.alert?.acknowledgedBy ?? null;
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alert.id
            ? { ...a, acknowledgedAt: ackAt, acknowledgedBy: ackBy }
            : a,
        ),
      );
      toast.success("Alert cleared");
    } catch (err) {
      if (isMounted.current) {
        toast.error(err.message || "Error clearing alert");
      }
    } finally {
      if (isMounted.current) {
        setClearingAlertIds((prev) => {
          const next = new Set(prev);
          next.delete(alert.id);
          return next;
        });
      }
    }
  };

  const panelStyle = {
    marginBottom: "24px",
    backgroundColor: enabled ? "#E8F5E9" : "#D9D9D9",
    color: "#333",
    padding: "16px",
    borderRadius: "8px",
    border: enabled ? "1px solid #C8E6C9" : "1px solid #BDBDBD",
    borderLeft: enabled ? "4px solid #4CAF50" : "4px solid #9E9E9E",
  };

  // GMA-1643: derive the freshness indicator from the newest READING time, not
  // the fetch time. A poll that only returns days-old readings must show
  // "Stale", never a green "Live · updated just now".
  const newestReading = newestRecordedAt(vitals);
  const freshness = vitalsFreshness(newestReading);
  const newestReadingIso =
    newestReading !== null ? new Date(newestReading).toISOString() : null;

  // GMA-821: the watch reports its own online status + battery to the presence
  // pipeline. Surface the Apple Watch device here so a caregiver knows whether
  // stale vitals mean "hasn't moved" vs "watch is dead / off the wrist".
  const presence = useUserPresence(clientId);
  const watchDevice =
    collapseDevicesByKind(parseClientDevices(presence?.devicesMap)).find(
      (d) => d.kind === DEVICE_KIND.WATCH,
    ) || null;

  // GMA-821: an "On/Off" badge overstates staleness — presence goes stale after
  // a few minutes of no heartbeat, which is normal for a backgrounded watch.
  // Show an honest status (On / Idle / Off) with the "as of" time so a caregiver
  // isn't told the watch is "Off" when it's just asleep on the wrist.
  const watchStatus = watchStatusLabel(watchDevice);
  const watchHasLastSeen =
    !!watchDevice &&
    typeof watchDevice.lastSeenMs === "number" &&
    Number.isFinite(watchDevice.lastSeenMs);
  const watchLastSeenRel = watchHasLastSeen
    ? formatRelativeTime(new Date(watchDevice.lastSeenMs).toISOString())
    : "";
  // "On" implies "now", so only append the relative time for Idle/Off.
  const showWatchLastSeen =
    !!watchDevice && !watchDevice.isOnline && watchHasLastSeen && !!watchLastSeenRel;
  const watchBadgeTitle = watchHasLastSeen
    ? `Live connection last seen ${watchLastSeenRel}. The watch may be on their wrist with the app asleep — this is not the same as powered off.`
    : "Live connection status is unknown. The watch may be on their wrist with the app asleep — this is not the same as powered off.";

  return (
    <div className="client-profile-section" style={panelStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <WatchIcon
            style={{ color: enabled ? "#4CAF50" : "#757575", fontSize: 28 }}
          />
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: "1.1rem" }}>
            Apple Watch
          </h2>
          {watchDevice && (
            <span
              data-testid="watch-power-badge"
              title={watchBadgeTitle}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.78rem",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                backgroundColor: watchDevice.isOnline ? "#E8F5E9" : "#EEEEEE",
                color: watchDevice.isOnline ? "#2E7D32" : "#616161",
                border: `1px solid ${watchDevice.isOnline ? "#A5D6A7" : "#BDBDBD"}`,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: watchDevice.isOnline ? "#4CAF50" : "#9E9E9E",
                }}
              />
              {watchStatus}
              {watchDevice.batteryLevel !== null && watchDevice.batteryLevel !== undefined && (
                <span
                  data-testid="watch-battery"
                  style={{
                    color: watchDevice.batteryLevel < 20 ? "#C62828" : "inherit",
                  }}
                >
                  {" · "}
                  {watchDevice.batteryLevel}%
                </span>
              )}
              {showWatchLastSeen && (
                <span
                  data-testid="watch-lastseen"
                  style={{ color: META_TEXT, fontWeight: 400 }}
                >
                  {" · "}
                  {watchLastSeenRel}
                </span>
              )}
            </span>
          )}
        </div>
        {canManageIntegration ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: toggling || !statusSettled ? "not-allowed" : "pointer",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "0.9rem", color: "#555" }}>
              {enabled ? "On for caregivers" : "Off for caregivers"}
            </span>
            <div
              role="switch"
              aria-checked={enabled}
              aria-disabled={toggling || !statusSettled}
              aria-label="Apple Watch integration"
              tabIndex={toggling || !statusSettled ? -1 : 0}
              onClick={toggling || !statusSettled ? undefined : handleToggle}
              onKeyDown={(e) => {
                if (
                  !toggling &&
                  statusSettled &&
                  (e.key === "Enter" || e.key === " " || e.key === "Spacebar")
                ) {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              style={{
                width: "48px",
                height: "26px",
                borderRadius: "13px",
                backgroundColor: enabled ? "#4CAF50" : "#BDBDBD",
                position: "relative",
                transition: "background-color 0.3s ease",
                opacity: toggling || !statusSettled ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: "2px",
                  left: enabled ? "24px" : "2px",
                  transition: "left 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
          </label>
        ) : null}
      </div>

      <p style={{ fontSize: "1rem", color: "#555", margin: "0 0 12px 0" }}>
        Real-time vitals from {clientName}&apos;s Apple Watch &mdash; heart rate,
        blood oxygen, respiratory rate, sleep, and more.
      </p>

      <ConsentStatusBadge
        clientId={clientId}
        sensorType="watch_health"
        enabled={enabled}
      />

      {enabled ? (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            {vitals.length > 0 && freshness === "live" ? (
              <span
                data-testid="vitals-freshness"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.85rem",
                  color: OK_TEXT,
                  fontWeight: 600,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                    display: "inline-block",
                  }}
                />
                Live
                <span style={{ color: META_TEXT, fontWeight: 400 }}>
                  &middot; updated {formatRelativeTime(newestReadingIso)}
                </span>
              </span>
            ) : vitals.length > 0 && freshness === "stale" ? (
              <span
                data-testid="vitals-freshness"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.85rem",
                  color: SEVERITY_TEXT_WARNING,
                  fontWeight: 600,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    backgroundColor: "#F5A623",
                    display: "inline-block",
                  }}
                />
                No recent Watch data
                <span style={{ color: "#616161", fontWeight: 400 }}>
                  &middot; last reading {formatRelativeTime(newestReadingIso)}
                </span>
              </span>
            ) : (
              // #459: loading/empty is owned by the paragraph below — no second
              // status badge here, so the card never shows two empty-state lines.
              null
            )}
          </div>

          {/* #586: the error renders ABOVE the readings, never instead of
              them — loadVitals keeps the last successful `vitals` through a
              failed poll, and a transient API failure must not take the
              caregiver's last-known readings off the screen. The tiles' own
              freshness tiers and the stale badge already date what is held. */}
          {loadError ? (
            <p
              style={{
                fontSize: "0.9rem",
                color: "#C62828",
                margin: vitals.length > 0 ? "0 0 10px 0" : 0,
              }}
            >
              Couldn&apos;t load vitals. Retrying automatically&hellip;
              {vitals.length > 0 ? " Showing the last readings received." : ""}
            </p>
          ) : null}
          {vitals.length === 0 ? (
            loadError ? null : (
              <p
                data-testid="watch-vitals-empty"
                style={{ fontSize: "0.9rem", color: META_TEXT, margin: 0 }}
              >
                {loading
                  ? "Loading vitals…"
                  : "No HealthKit vitals for this client yet. Readings appear here once their Apple Watch or iPhone Health data syncs."}
              </p>
            )
          ) : (
            <>
              <div
                data-testid="vitals-freshness-legend"
                role="note"
                aria-label="Freshness key for the readings below"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "10px",
                  fontSize: "0.75rem",
                  color: META_TEXT,
                }}
              >
                {FRESHNESS_LEGEND.map(({ tier, label }) => (
                  <span
                    key={tier}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: "9px",
                        height: "9px",
                        borderRadius: "50%",
                        backgroundColor: FRESHNESS_COLORS[tier],
                        display: "inline-block",
                      }}
                    />
                    {label}
                  </span>
                ))}
                <span style={{ flexBasis: "100%", color: META_TEXT }}>
                  {FRESHNESS_LEGEND_NOTE}
                </span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "10px",
                }}
              >
              {vitals.map((m) => {
                const desc = metricDescription(m.metricType);
                // GMA-1879: WHEN the watch takes this reading. Every described
                // metric carries one, including the frequently-sampled ones —
                // there the fact is what makes a gap worth escalating rather
                // than what excuses it.
                const whenMeasured = metricWhenMeasured(m.metricType);
                // Per-tile freshness — a sparse gait/stair reading can be days
                // old next to a live heart rate; color-code so the caregiver
                // sees which numbers are current at a glance.
                const tier = metricFreshnessTier(m.recordedAt);
                const tierColor = FRESHNESS_COLORS[tier];
                const ageText = m.recordedAt
                  ? `updated ${formatRelativeTime(m.recordedAt)}`
                  : "";
                const rangeLevel =
                  m.metricType === BP_PAIRED
                    ? classifyBloodPressureSeverity(m.value, customThresholds)
                    : typeof m.value === "number"
                      ? classifyVitalSeverity(
                          m.value,
                          m.metricType,
                          customThresholds,
                        )
                      : "unclassified";
                // Unbanded metrics (body temperature, gait, HRV…) are
                // 'unclassified' — neutral text, never a clinical green.
                const rangeColor =
                  vitalRangeColor(rangeLevel, { surface: "light" }) || "#333";
                const rangeMarker = vitalSeverityMarker(rangeLevel);
                const rangeA11y = vitalSeverityA11yLabel(rangeLevel);
                // aria-label pairs the plain-language explanation with the
                // clinical band and freshness time for screen readers (color
                // is never the only signal); the MUI Tooltip shows the
                // explanation on hover/touch.
                const ariaText = [desc, whenMeasured, rangeA11y, ageText]
                  .filter(Boolean)
                  .join(" · ");
                const tile = (
                  <div
                    key={m.metricType}
                    // a11y: a bare <div> has no role that accepts an accessible
                    // name, so `aria-label` on one is not reliably exposed — the
                    // caregiver explanation and the reading's age can be dropped
                    // and only the visible number announced. `group` supports
                    // naming and adds no interaction semantics to a static tile.
                    role="group"
                    data-testid={`vital-${m.metricType}`}
                    data-freshness={tier}
                    data-vital-severity={rangeLevel}
                    aria-label={ariaText || undefined}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      border: "1px solid #C8E6C9",
                      borderLeft: `4px solid ${tierColor}`,
                      padding: "10px 12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.78rem",
                        color: META_TEXT,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {metricLabel(m.metricType)}
                    </div>
                    <div
                      style={{
                        fontSize: "1.35rem",
                        fontWeight: 700,
                        color: rangeColor,
                        lineHeight: 1.3,
                      }}
                    >
                      {formatMetricValue(m.value)}
                      {m.unit ? (
                        <span
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            color: META_TEXT,
                            marginLeft: "4px",
                          }}
                        >
                          {m.unit}
                        </span>
                      ) : null}
                      {/* Severity is never color-only: visible marker (the
                          word itself is announced via the tile aria-label). */}
                      {rangeMarker ? (
                        <span
                          aria-hidden="true"
                          style={{
                            marginLeft: "5px",
                            fontWeight: 700,
                            color: rangeColor,
                          }}
                        >
                          {rangeMarker}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: META_TEXT }}>
                      {formatRelativeTime(m.recordedAt)}
                    </div>
                  </div>
                );
                // Wrap when there is ANY explanatory text. This was
                // description-only; the when-measured note now also qualifies,
                // and an empty MUI Tooltip is still worth avoiding.
                //
                // Both are checked rather than just the description even though
                // the parity test currently forbids a note without one: that
                // test constrains the two maps, not this render path, and a
                // condition that silently depends on another file's invariant
                // breaks quietly the day the invariant is relaxed.
                return desc || whenMeasured ? (
                  <Tooltip
                    key={m.metricType}
                    title={[desc, whenMeasured].filter(Boolean).join(" ")}
                    enterTouchDelay={0}
                    arrow
                  >
                    {tile}
                  </Tooltip>
                ) : (
                  tile
                );
              })}
              </div>
            </>
          )}

          {/* GMA-1579: recent abnormal-vitals alerts (email/push were sent;
              this is the web caregiver's in-app view of them). */}
          {visibleAlerts(alerts).length > 0 ? (
            <div style={{ marginTop: "16px" }} data-testid="vitals-alerts">
              <div
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#555",
                  marginBottom: "8px",
                }}
              >
                Recent alerts
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {groupVisibleAlerts(visibleAlerts(alerts), alerts.length).map((group) => (
                  <div key={group.key} data-testid={`vitals-alert-group-${group.key}`}>
                    {/* #490 gap 3: a repeat run collapses to one summary row.
                        A run of one renders exactly as it always did. */}
                    {group.count > 1 ? (
                      <AlertGroupSummary
                        group={group}
                        expanded={expandedAlertKeys.has(group.key)}
                        onToggle={() => toggleAlertGroup(group.key)}
                      />
                    ) : null}
                    {group.count === 1 || expandedAlertKeys.has(group.key) ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          ...(group.count > 1
                            ? { marginTop: "6px", marginLeft: "16px" }
                            : {}),
                        }}
                      >
                        {group.members.map((a) => {
                  const critical = a.severity === "critical";
                  const clearing = clearingAlertIds.has(a.id);
                  const accent = critical ? SEVERITY_ACCENT_CRITICAL : SEVERITY_ACCENT_WARNING;
  const severityText = critical ? SEVERITY_TEXT_CRITICAL : SEVERITY_TEXT_WARNING;
                  return (
                    <div
                      key={a.id}
                      data-testid={`vitals-alert-${a.id}`}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "8px",
                        backgroundColor: "#fff",
                        border: "1px solid #eee",
                        borderLeft: `4px solid ${accent}`,
                        borderRadius: "6px",
                        padding: "8px 10px",
                        fontSize: "0.85rem",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: "0.7rem",
                          color: severityText,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {critical ? "Critical" : "Warning"}
                      </span>
                      <span style={{ color: "#333", flex: 1 }}>
                        {a.message || metricLabel(a.metricType)}
                      </span>
                      <span style={{ color: META_TEXT, fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                        {formatRelativeTime(a.alertedAt)}
                      </span>
                      {/* Issue #633: clear is recorded server-side (who + when)
                          for audit — the row is hidden here, never deleted. */}
                      <button
                        type="button"
                        aria-label={`Clear alert: ${a.message || metricLabel(a.metricType)}`}
                        disabled={clearing}
                        onClick={() => handleClearAlert(a)}
                        style={{
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fafafa",
                          color: "#555",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          padding: "2px 8px",
                          cursor: clearing ? "not-allowed" : "pointer",
                          opacity: clearing ? 0.6 : 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {clearing ? "Clearing…" : "Clear"}
                      </button>
                    </div>
                  );
                        })}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* GMA-1581: categorical HealthKit signals (ECG, AFib, irregular
              rhythm, sleep-apnea / hypertension notifications, sleep stage). */}
          {signals.length > 0 ? (
            <div style={{ marginTop: "16px" }} data-testid="health-signals">
              <div
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#555",
                  marginBottom: "8px",
                }}
              >
                Health signals
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {signals.map((s) => {
                  const notable = isNotableSignal(s);
                  const accent = notable ? SEVERITY_ACCENT_WARNING : "#C8E6C9";
                  return (
                    <div
                      key={s.signal_type}
                      data-testid={`health-signal-${s.signal_type}`}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "6px",
                        backgroundColor: "#fff",
                        border: "1px solid #eee",
                        borderLeft: `4px solid ${accent}`,
                        borderRadius: "6px",
                        padding: "6px 10px",
                        fontSize: "0.8rem",
                      }}
                    >
                      <span style={{ fontWeight: 700, color: "#333" }}>
                        {signalLabel(s.signal_type)}
                      </span>
                      {s.category ? (
                        <span style={{ color: notable ? SEVERITY_TEXT_WARNING : META_TEXT }}>
                          {s.category}
                        </span>
                      ) : null}
                      {typeof s.value === "number" ? (
                        <span style={{ color: META_TEXT }}>
                          {s.value}
                          {s.unit || ""}
                        </span>
                      ) : null}
                      <span style={{ color: META_TEXT, fontSize: "0.7rem", whiteSpace: "nowrap" }}>
                        {formatRelativeTime(s.recorded_at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* GMA-1588: admin-only per-client alert threshold sliders. onSaved
              refreshes this section's once-per-client threshold copy so tile
              colors track an admin's edits without polling the admin-only
              config endpoint every vitals cycle. */}
          <WatchThresholdSettings
            clientId={clientId}
            canManage={canManageIntegration}
            onSaved={loadThresholds}
          />
        </div>
      ) : (
        <p style={{ fontSize: "0.9rem", color: DISABLED_PANEL_TEXT, margin: 0 }}>
          {canManageIntegration
            ? "Enable this feature to show this client’s live Apple Watch vitals to admins and assigned caregivers."
            : "This feature is not yet enabled for this client. Ask an admin to enable Apple Watch monitoring."}
        </p>
      )}
    </div>
  );
}
